import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  Holiday,
  CreateHolidayDto,
  Zone,
  GlobalSettingsDto,
  BookingConflict,
  readBookingConflict
} from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { PermissionService, CC_PERMISSIONS } from '../../services/permission.service';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { BookingConflictPanelComponent } from '../shared/booking-conflict-panel/booking-conflict-panel.component';

@Component({
  selector: 'app-holidays',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HasPermissionDirective, BookingConflictPanelComponent],
  templateUrl: './holidays.component.html',
  styleUrl: './holidays.component.css'
})
export class HolidaysComponent implements OnInit, OnDestroy {
  readonly P = CC_PERMISSIONS;

  holidays: Holiday[] = [];
  filteredHolidays: Holiday[] = [];
  zones: Zone[] = [];
  timeSlotOptions: string[] = [];

  isLoading = false;
  isSubmitting = false;
  showCreateModal = false;
  createConflict: BookingConflict | null = null;
  deletingId: string | null = null;

  // Filter criteria
  searchQuery = '';
  selectedZoneFilter = '';
  fromDateFilter = '';
  toDateFilter = '';
  filterType = 'all'; // 'all', 'upcoming', 'past'

  // Create form model
  holidayForm = {
    name: '',
    date: '',
    geoZoneId: '',
    timeSlot: ''
  };

  formErrors: { [key: string]: string } = {};

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  private destroy$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    public permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // Set default date range to cover the current year (from Jan 1 to Dec 31)
    const now = new Date();
    const currentYear = now.getFullYear();
    this.fromDateFilter = `${currentYear}-01-01`;
    this.toDateFilter = `${currentYear}-12-31`;

    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadZones();
        this.loadTimeSlots();
        this.loadHolidays();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadZones(): void {
    this.externalDataService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones.filter(z => !z.disabled && !z.deletedAt);
        },
        error: (err) => {
          console.error('Failed to load geo zones', err);
        }
      });
  }

  loadTimeSlots(): void {
    this.externalDataService.getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings: GlobalSettingsDto) => {
          if (settings?.appointmentTimeSlots && settings.appointmentTimeSlots.length > 0) {
            this.timeSlotOptions = settings.appointmentTimeSlots;
          } else {
            this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
          }
        },
        error: (err) => {
          console.error('Failed to load global settings time slots', err);
          this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
        }
      });
  }

  loadHolidays(): void {
    this.isLoading = true;
    const filter = {
      fromDate: this.fromDateFilter ? new Date(this.fromDateFilter).toISOString() : null,
      toDate: this.toDateFilter ? new Date(this.toDateFilter + 'T23:59:59').toISOString() : null,
      pageIndex: 0,
      pageSize: 200
    };

    const zoneId = this.selectedZoneFilter ? this.selectedZoneFilter : undefined;

    this.externalDataService.getHolidays(filter, zoneId, 'Holiday')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (holidays) => {
          // Filter out internal Capacity blocks (if any) and sort by date ascending
          this.holidays = (holidays || [])
            .filter(h => h.type !== 'Capacity')
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          this.applyFilters();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load holidays', err);
          this.showNotification('Failed to load holidays. Please try again.', 'error');
          this.isLoading = false;
        }
      });
  }

  applyFilters(): void {
    const query = this.searchQuery.trim().toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.filteredHolidays = this.holidays.filter(holiday => {
      const matchesSearch = !query || holiday.name.toLowerCase().includes(query);

      const holidayDate = new Date(holiday.date);
      holidayDate.setHours(0, 0, 0, 0);

      let matchesTimeline = true;
      if (this.filterType === 'upcoming') {
        matchesTimeline = holidayDate.getTime() >= today.getTime();
      } else if (this.filterType === 'past') {
        matchesTimeline = holidayDate.getTime() < today.getTime();
      }

      return matchesSearch && matchesTimeline;
    });
  }

  onFilterChange(): void {
    this.loadHolidays();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  setFilterType(type: 'all' | 'upcoming' | 'past'): void {
    this.filterType = type;
    this.applyFilters();
  }

  openCreateModal(): void {
    const todayStr = new Date().toISOString().split('T')[0];
    this.holidayForm = {
      name: '',
      date: todayStr,
      geoZoneId: '',
      timeSlot: ''
    };
    this.formErrors = {};
    this.createConflict = null;
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.formErrors = {};
    this.createConflict = null;
  }

  deleteHoliday(holiday: Holiday): void {
    if (this.deletingId || holiday.type === 'Capacity') {
      return;
    }
    const when = new Date(holiday.date).toDateString();
    if (!confirm(`Delete holiday "${holiday.name}" on ${when}? Customers will be able to book this date again.`)) {
      return;
    }

    this.deletingId = holiday.id;
    this.externalDataService.deleteHoliday(holiday)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingId = null;
          this.showNotification(`Holiday "${holiday.name}" deleted.`, 'success');
          this.loadHolidays();
        },
        error: (err) => {
          console.error('Failed to delete holiday', err);
          this.deletingId = null;
          this.showNotification(err?.error?.detail || 'Failed to delete holiday. Please try again.', 'error');
        }
      });
  }

  validateForm(): boolean {
    this.formErrors = {};

    if (!this.holidayForm.name || !this.holidayForm.name.trim()) {
      this.formErrors['name'] = 'Holiday name is required';
    } else if (this.holidayForm.name.trim().length < 2) {
      this.formErrors['name'] = 'Holiday name must be at least 2 characters';
    } else if (this.holidayForm.name.trim().length > 150) {
      this.formErrors['name'] = 'Holiday name must be less than 150 characters';
    }

    if (!this.holidayForm.date) {
      this.formErrors['date'] = 'Holiday date is required';
    }

    return Object.keys(this.formErrors).length === 0;
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;
    this.createConflict = null;

    // Convert date string to ISO DateTime
    const selectedDate = new Date(this.holidayForm.date);

    const payload: CreateHolidayDto = {
      name: this.holidayForm.name.trim(),
      type: 'Holiday',
      date: selectedDate.toISOString(),
      geoZoneId: this.holidayForm.geoZoneId ? this.holidayForm.geoZoneId : null,
      timeSlot: this.holidayForm.timeSlot ? this.holidayForm.timeSlot : null
    };

    this.externalDataService.createHoliday(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.closeCreateModal();
          this.showNotification(`Holiday "${payload.name}" created successfully.`, 'success');
          this.loadHolidays();
        },
        error: (err) => {
          console.error('Failed to create holiday', err);
          this.isSubmitting = false;
          this.createConflict = readBookingConflict(err);
          if (this.createConflict) {
            return;
          }
          const msg = err?.error?.detail || err?.error?.message || err?.message || 'Failed to create holiday. Please try again.';
          this.showNotification(msg, 'error');
        }
      });
  }

  getZoneName(zoneId: string | null): string {
    if (!zoneId) return 'Global (All Zones)';
    const found = this.zones.find(z => z.id === zoneId);
    return found ? found.name : 'Specific Zone';
  }

  getTimeSlotLabel(slot: string | null): string {
    if (!slot) return 'Full Day (All Slots)';
    return slot;
  }

  isPast(dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
  }

  isToday(dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  }

  isUpcoming(dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date.getTime() > today.getTime();
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    this.notification = {
      show: true,
      message,
      type
    };

    setTimeout(() => {
      this.notification.show = false;
    }, 4000);
  }

  dismissNotification(): void {
    this.notification.show = false;
  }
}
